import { useEffect, useState, useCallback, useRef } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, $isTextNode, TextNode, type LexicalEditor } from "lexical";
import { createPortal } from "react-dom";
import styles from "./SuggestionPlugin.module.css";

export type SuggestionCategory = "grammar" | "clarity" | "structure" | "style" | "logic";

export interface Suggestion {
  id: string;
  textRange: { start: number; end: number };
  originalText: string;
  suggestedText: string;
  category: SuggestionCategory;
  confidenceScore: number;
  explanation?: string;
}

export interface SuggestionPluginProps {
  suggestions: Suggestion[];
  onAccept?: (suggestion: Suggestion) => void;
  onReject?: (suggestion: Suggestion) => void;
}

interface DecorationPosition {
  suggestion: Suggestion;
  rect: DOMRect;
}

function findTextNodeAtOffset(
  editor: LexicalEditor,
  targetOffset: number,
): { node: TextNode; localOffset: number } | null {
  let result: { node: TextNode; localOffset: number } | null = null;

  editor.getEditorState().read(() => {
    const root = $getRoot();
    let currentOffset = 0;

    const processNode = (node: TextNode | null): boolean => {
      if (!node) return false;

      if ($isTextNode(node)) {
        const textContent = node.getTextContent();
        const nodeLength = textContent.length;

        if (currentOffset + nodeLength > targetOffset) {
          result = {
            node,
            localOffset: targetOffset - currentOffset,
          };
          return true;
        }
        currentOffset += nodeLength + 1;
      }
      return false;
    };

    const traverse = (node: ReturnType<typeof $getRoot>): boolean => {
      const children = node.getChildren();
      for (const child of children) {
        if ($isTextNode(child)) {
          if (processNode(child)) return true;
        } else if ("getChildren" in child && typeof child.getChildren === "function") {
          if (traverse(child as ReturnType<typeof $getRoot>)) return true;
        }
      }
      return false;
    };

    traverse(root);
  });

  return result;
}

function SuggestionTooltip({
  suggestion,
  position,
  onAccept,
  onReject,
  onClose,
}: {
  suggestion: Suggestion;
  position: { top: number; left: number };
  onAccept?: (suggestion: Suggestion) => void;
  onReject?: (suggestion: Suggestion) => void;
  onClose: () => void;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const getCategoryColor = (category: SuggestionCategory): string => {
    const colors: Record<SuggestionCategory, string> = {
      grammar: "var(--color-suggest-grammar)",
      clarity: "var(--color-suggest-clarity)",
      structure: "var(--color-suggest-structure)",
      style: "var(--color-suggest-style)",
      logic: "var(--color-suggest-logic)",
    };
    return colors[category];
  };

  const getConfidenceLevel = (score: number): string => {
    if (score >= 0.8) return styles.confidenceHigh;
    if (score >= 0.5) return styles.confidenceMedium;
    return styles.confidenceLow;
  };

  return createPortal(
    <div
      ref={tooltipRef}
      className={styles.tooltip}
      style={{
        top: position.top,
        left: position.left,
        borderColor: getCategoryColor(suggestion.category),
      }}
    >
      <div className={styles.tooltipHeader}>
        <span
          className={styles.categoryBadge}
          style={{ backgroundColor: getCategoryColor(suggestion.category) }}
        >
          {suggestion.category}
        </span>
        <div className={styles.confidenceContainer}>
          <span className={styles.confidence}>{Math.round(suggestion.confidenceScore * 100)}%</span>
          <div className={styles.confidenceBar}>
            <div
              className={`${styles.confidenceFill} ${getConfidenceLevel(suggestion.confidenceScore)}`}
              style={{ width: `${suggestion.confidenceScore * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className={styles.tooltipContent}>
        <div className={styles.original}>
          <span className={styles.label}>Original:</span>
          <span className={styles.text}>{suggestion.originalText}</span>
        </div>
        <div className={styles.suggested}>
          <span className={styles.label}>Suggested:</span>
          <span className={styles.text}>{suggestion.suggestedText}</span>
        </div>
        {suggestion.explanation && <p className={styles.explanation}>{suggestion.explanation}</p>}
      </div>

      <div className={styles.tooltipActions}>
        <button
          type="button"
          className={styles.acceptButton}
          onClick={() => {
            onAccept?.(suggestion);
            onClose();
          }}
        >
          Accept
        </button>
        <button
          type="button"
          className={styles.rejectButton}
          onClick={() => {
            onReject?.(suggestion);
            onClose();
          }}
        >
          Reject
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function SuggestionPlugin({ suggestions, onAccept, onReject }: SuggestionPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [decorations, setDecorations] = useState<DecorationPosition[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState<{
    suggestion: Suggestion;
    position: { top: number; left: number };
  } | null>(null);
  const editorRootRef = useRef<HTMLElement | null>(null);

  const updateDecorations = useCallback(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    editorRootRef.current = rootElement;

    const newDecorations: DecorationPosition[] = [];

    for (const suggestion of suggestions) {
      const startResult = findTextNodeAtOffset(editor, suggestion.textRange.start);

      if (startResult) {
        editor.getEditorState().read(() => {
          const element = editor.getElementByKey(startResult.node.getKey());
          if (element) {
            const range = document.createRange();
            const textNode = element.firstChild;

            if (textNode && textNode.nodeType === Node.TEXT_NODE) {
              const startOffset = Math.min(
                startResult.localOffset,
                textNode.textContent?.length ?? 0,
              );
              const endOffset = Math.min(
                startResult.localOffset + suggestion.originalText.length,
                textNode.textContent?.length ?? 0,
              );

              if (startOffset < endOffset) {
                range.setStart(textNode, startOffset);
                range.setEnd(textNode, endOffset);

                const rects = range.getClientRects();
                if (rects.length > 0) {
                  newDecorations.push({
                    suggestion,
                    rect: rects[0],
                  });
                }
              }
            }
          }
        });
      }
    }

    setDecorations(newDecorations);
  }, [editor, suggestions]);

  useEffect(() => {
    updateDecorations();

    return editor.registerUpdateListener(() => {
      updateDecorations();
    });
  }, [editor, updateDecorations]);

  useEffect(() => {
    const handleScroll = () => updateDecorations();
    const handleResize = () => updateDecorations();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [updateDecorations]);

  const handleDecorationClick = (decoration: DecorationPosition) => {
    setActiveSuggestion({
      suggestion: decoration.suggestion,
      position: {
        top: decoration.rect.bottom + window.scrollY + 8,
        left: decoration.rect.left + window.scrollX,
      },
    });
  };

  const getCategoryColor = (category: SuggestionCategory): string => {
    const colors: Record<SuggestionCategory, string> = {
      grammar: "var(--color-suggest-grammar)",
      clarity: "var(--color-suggest-clarity)",
      structure: "var(--color-suggest-structure)",
      style: "var(--color-suggest-style)",
      logic: "var(--color-suggest-logic)",
    };
    return colors[category];
  };

  const handleAccept = useCallback(
    (suggestion: Suggestion) => {
      editor.update(() => {
        const startResult = findTextNodeAtOffset(editor, suggestion.textRange.start);
        if (startResult && $isTextNode(startResult.node)) {
          const textContent = startResult.node.getTextContent();
          const before = textContent.slice(0, startResult.localOffset);
          const after = textContent.slice(startResult.localOffset + suggestion.originalText.length);

          startResult.node.setTextContent(before + suggestion.suggestedText + after);
        }
      });

      onAccept?.(suggestion);
    },
    [editor, onAccept],
  );

  return (
    <>
      {decorations.map((decoration) =>
        createPortal(
          <div
            key={decoration.suggestion.id}
            className={styles.decoration}
            style={{
              position: "fixed",
              top: decoration.rect.top,
              left: decoration.rect.left,
              width: decoration.rect.width,
              height: decoration.rect.height,
              backgroundColor: getCategoryColor(decoration.suggestion.category),
              opacity: 0.2,
              pointerEvents: "auto",
              cursor: "pointer",
              borderRadius: "2px",
            }}
            onClick={() => handleDecorationClick(decoration)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleDecorationClick(decoration);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Suggestion: ${decoration.suggestion.category}`}
          />,
          document.body,
        ),
      )}

      {activeSuggestion && (
        <SuggestionTooltip
          suggestion={activeSuggestion.suggestion}
          position={activeSuggestion.position}
          onAccept={handleAccept}
          onReject={onReject}
          onClose={() => setActiveSuggestion(null)}
        />
      )}
    </>
  );
}
